import React, { Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GraduationCap, Cpu, BarChart3, Brain, Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useOrganization } from "@/contexts/OrganizationContext";

const SkillAnalytics = React.lazy(() => import("./SkillAnalytics"));
const AIUsage = React.lazy(() => import("./AIUsage"));
const SandboxUsage = React.lazy(() => import("./SandboxUsage"));
const KnowledgeBase = React.lazy(() => import("./KnowledgeBase"));

const TabLoadingFallback = () => (
  <div className="flex items-center justify-center p-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const AnalyticsHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization } = useOrganization();
  const activeTab = searchParams.get("tab") || "skills";

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const showSandbox = organization?.settings?.enable_sandbox !== false;

  return (
    <div className="h-full">
      <div className="px-4 pt-4 md:px-8 md:pt-6">
        <h1 className="text-2xl font-bold font-display mb-4">Analytics</h1>
      </div>
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="px-4 md:px-8">
          <TabsList className={`w-full md:w-auto grid md:inline-flex ${showSandbox ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <TabsTrigger value="skills" className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              <span className="hidden sm:inline">Skills</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Cpu className="h-4 w-4" />
              <span className="hidden sm:inline">AI Usage</span>
            </TabsTrigger>
            {showSandbox && (
              <TabsTrigger value="planner" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">Planner</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="knowledge" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              <span className="hidden sm:inline">Knowledge</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="skills" className="mt-0 p-4 md:p-8">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <SkillAnalytics embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="ai" className="mt-0 p-4 md:p-8">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <AIUsage embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        {showSandbox && (
          <TabsContent value="planner" className="mt-0 p-4 md:p-8">
            <ErrorBoundary>
              <Suspense fallback={<TabLoadingFallback />}>
                <SandboxUsage embedded />
              </Suspense>
            </ErrorBoundary>
          </TabsContent>
        )}
        <TabsContent value="knowledge" className="mt-0 p-4 md:p-8">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <KnowledgeBase embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsHub;
